import React, { useContext, useMemo, useState, useCallback } from 'react';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Menu from '@mui/material/Menu';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import TablePagination from '@mui/material/TablePagination';
import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import ReplayIcon from '@mui/icons-material/Replay';
import CancelIcon from '@mui/icons-material/CancelOutlined';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { styled } from '@mui/material/styles';
import { DatePicker } from '@mui/x-date-pickers';
import { DateTime } from 'luxon';
import { RouteComponentProps } from '@reach/router';
import { useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import {
  ApplicationContext,
  callListJobs,
  callRetryJob,
  callCancelJob
} from '@particles';
import type { MyJobItem, ListJobsResponse } from '../../../particles/api/core/api/callGetMyJobs';
import { Table } from '@organisms';
import { columns, generateRows, JOB_TYPE_LABELS, STATUS_LABELS, STATUS_COLORS } from './particles';

const StyledContainer = styled(Container)(({ theme }) => ({
  padding: theme.spacing(3)
}));

const JOB_TYPE_OPTIONS = Object.entries(JOB_TYPE_LABELS);
const STATUS_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'pending', label: STATUS_LABELS.pending },
  { value: 'running', label: STATUS_LABELS.running },
  { value: 'completed', label: STATUS_LABELS.completed },
  { value: 'failed', label: STATUS_LABELS.failed },
  { value: 'cancelled', label: STATUS_LABELS.cancelled }
];

const SAJobs: React.FC<RouteComponentProps> = () => {
  const queryClient = useQueryClient();
  const { state } = useContext(ApplicationContext);
  const currentUserId = state?.user?.id;

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [jobType, setJobType] = useState<string>('');
  const [status, setStatus] = useState<string>('');
  const [dateFrom, setDateFrom] = useState<DateTime | null>(null);
  const [dateTo, setDateTo] = useState<DateTime | null>(null);
  const [onlyMine, setOnlyMine] = useState(false);

  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [menuJob, setMenuJob] = useState<MyJobItem | null>(null);
  const [drawerJob, setDrawerJob] = useState<MyJobItem | null>(null);

  const queryParams = useMemo(
    () => ({
      limit: rowsPerPage,
      offset: page * rowsPerPage,
      jobType: jobType ? [jobType] : undefined,
      status: status ? [status] : undefined,
      dateFrom: dateFrom?.isValid ? dateFrom.startOf('day').toISO() ?? undefined : undefined,
      dateTo: dateTo?.isValid ? dateTo.endOf('day').toISO() ?? undefined : undefined,
      createdBy: onlyMine && currentUserId ? [currentUserId] : undefined
    }),
    [rowsPerPage, page, jobType, status, dateFrom, dateTo, onlyMine, currentUserId]
  );

  const { data, isFetching, refetch } = useQuery<ListJobsResponse>({
    queryKey: ['tasks-list', queryParams],
    queryFn: async () => {
      const res = await callListJobs(queryParams);
      if (res.code !== 200 || !res.data) throw new Error('Failed to load tasks');
      return res.data;
    },
    placeholderData: keepPreviousData,
    staleTime: 30 * 1000
  });

  const jobs = data?.jobs ?? [];
  const total = data?.total ?? 0;

  const handleMenuOpen = useCallback((job: MyJobItem, event: React.MouseEvent<HTMLElement>) => {
    setMenuJob(job);
    setMenuAnchorEl(event.currentTarget);
  }, []);

  const handleMenuClose = useCallback(() => {
    setMenuAnchorEl(null);
    setMenuJob(null);
  }, []);

  const rows = useMemo(
    () => generateRows(jobs, { onMenuOpen: handleMenuOpen }),
    [jobs, handleMenuOpen]
  );

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['tasks-list'] });
  }, [queryClient]);

  const handleView = useCallback(() => {
    if (menuJob) setDrawerJob(menuJob);
    handleMenuClose();
  }, [menuJob, handleMenuClose]);

  const handleRetry = useCallback(async () => {
    const target = menuJob;
    handleMenuClose();
    if (!target) return;
    const res = await callRetryJob(target.jobId);
    if (res.code === 200 || res.code === 202) {
      toast.success('Task re-enqueued');
      invalidate();
    } else {
      toast.error((res as any)?.message || 'Failed to retry task');
    }
  }, [menuJob, handleMenuClose, invalidate]);

  const handleCancel = useCallback(async () => {
    const target = menuJob;
    handleMenuClose();
    if (!target) return;
    const res = await callCancelJob(target.jobId);
    if (res.code === 200) {
      toast.success('Task cancelled');
      invalidate();
    } else {
      toast.error((res as any)?.message || 'Failed to cancel task');
    }
  }, [menuJob, handleMenuClose, invalidate]);

  const handleResetFilters = () => {
    setJobType('');
    setStatus('');
    setDateFrom(null);
    setDateTo(null);
    setOnlyMine(false);
    setPage(0);
  };

  const canRetry = !!menuJob && (menuJob.status === 'failed' || menuJob.status === 'cancelled');
  const canCancel = !!menuJob && menuJob.status === 'pending';

  return (
    <StyledContainer maxWidth="xl">
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2, flexWrap: 'wrap', rowGap: 2 }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Type</InputLabel>
          <Select
            label="Type"
            value={jobType}
            onChange={(e) => {
              setPage(0);
              setJobType(e.target.value as string);
            }}
          >
            <MenuItem value="">All types</MenuItem>
            {JOB_TYPE_OPTIONS.map(([value, label]) => (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Status</InputLabel>
          <Select
            label="Status"
            value={status}
            onChange={(e) => {
              setPage(0);
              setStatus(e.target.value as string);
            }}
          >
            <MenuItem value="">All statuses</MenuItem>
            {STATUS_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <DatePicker
          label="From"
          format="dd/MM/yyyy"
          value={dateFrom}
          onChange={(v) => {
            setPage(0);
            setDateFrom(v);
          }}
          maxDate={dateTo ?? undefined}
          slotProps={{ textField: { size: 'small', sx: { minWidth: 160 } } }}
        />

        <DatePicker
          label="To"
          format="dd/MM/yyyy"
          value={dateTo}
          onChange={(v) => {
            setPage(0);
            setDateTo(v);
          }}
          minDate={dateFrom ?? undefined}
          slotProps={{ textField: { size: 'small', sx: { minWidth: 160 } } }}
        />

        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={onlyMine}
              onChange={(_, checked) => {
                setPage(0);
                setOnlyMine(checked);
              }}
            />
          }
          label="Only my tasks"
        />

        <Button size="small" onClick={handleResetFilters}>
          Reset
        </Button>

        <Box sx={{ flex: 1 }} />

        <IconButton
          size="small"
          onClick={() => refetch()}
          title="Refresh"
        >
          <RefreshIcon />
        </IconButton>
      </Stack>

      <Table
        name="jobs"
        columns={columns}
        data={rows}
        isLoading={isFetching}
        noResultsMessage="No tasks match these filters"
        disablePageSize
        persistColumnPrefs
      />

      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[10, 25, 50, 100]}
      />

      <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={handleView}>
          <ListItemIcon>
            <VisibilityIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View details</ListItemText>
        </MenuItem>
        {canRetry && (
          <>
            <Divider />
            <MenuItem onClick={handleRetry}>
              <ListItemIcon>
                <ReplayIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Retry</ListItemText>
            </MenuItem>
          </>
        )}
        {canCancel && (
          <>
            <Divider />
            <MenuItem onClick={handleCancel}>
              <ListItemIcon>
                <CancelIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Cancel</ListItemText>
            </MenuItem>
          </>
        )}
      </Menu>

      <Drawer
        anchor="right"
        open={Boolean(drawerJob)}
        onClose={() => setDrawerJob(null)}
        PaperProps={{ sx: { width: { xs: '100%', sm: 480 } } }}
      >
        {drawerJob && (
          <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
              <Typography variant="h6">Task details</Typography>
              <IconButton onClick={() => setDrawerJob(null)} size="small">
                <CloseIcon />
              </IconButton>
            </Stack>

            <Stack spacing={2} sx={{ flex: 1, overflowY: 'auto' }}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Type
                </Typography>
                <Typography variant="body2">
                  {JOB_TYPE_LABELS[drawerJob.jobType] || drawerJob.jobType}
                </Typography>
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary">
                  Status
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  <Chip
                    size="small"
                    label={STATUS_LABELS[drawerJob.status] || drawerJob.status}
                    sx={{
                      backgroundColor: STATUS_COLORS[drawerJob.status] || '#9e9e9e',
                      color: 'white'
                    }}
                  />
                </Box>
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary">
                  Task ID
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                  {drawerJob.jobId}
                </Typography>
              </Box>

              {drawerJob.createdBy && (
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Created by
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                    {drawerJob.createdBy}
                  </Typography>
                </Box>
              )}

              <Box>
                <Typography variant="caption" color="text.secondary">
                  Created
                </Typography>
                <Typography variant="body2">
                  {drawerJob.createdAt
                    ? DateTime.fromISO(drawerJob.createdAt).toLocaleString(DateTime.DATETIME_MED)
                    : '—'}
                </Typography>
              </Box>

              {drawerJob.startedAt && (
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Started
                  </Typography>
                  <Typography variant="body2">
                    {DateTime.fromISO(drawerJob.startedAt).toLocaleString(DateTime.DATETIME_MED)}
                  </Typography>
                </Box>
              )}

              {drawerJob.completedAt && (
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Completed
                  </Typography>
                  <Typography variant="body2">
                    {DateTime.fromISO(drawerJob.completedAt).toLocaleString(DateTime.DATETIME_MED)}
                  </Typography>
                </Box>
              )}

              {drawerJob.errorMessage && (
                <Box>
                  <Typography variant="caption" color="error">
                    Error
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: 'error.main', whiteSpace: 'pre-wrap' }}
                  >
                    {drawerJob.errorMessage}
                  </Typography>
                </Box>
              )}

              {drawerJob.payload && (
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Payload
                  </Typography>
                  <Box
                    component="pre"
                    sx={{
                      mt: 0.5,
                      p: 1.5,
                      backgroundColor: 'grey.100',
                      borderRadius: 1,
                      fontSize: '0.75rem',
                      fontFamily: 'monospace',
                      overflowX: 'auto',
                      maxHeight: 240
                    }}
                  >
                    {JSON.stringify(drawerJob.payload, null, 2)}
                  </Box>
                </Box>
              )}

              {drawerJob.result && Object.keys(drawerJob.result).length > 0 && (
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Result
                  </Typography>
                  <Box
                    component="pre"
                    sx={{
                      mt: 0.5,
                      p: 1.5,
                      backgroundColor: 'grey.100',
                      borderRadius: 1,
                      fontSize: '0.75rem',
                      fontFamily: 'monospace',
                      overflowX: 'auto',
                      maxHeight: 240
                    }}
                  >
                    {JSON.stringify(drawerJob.result, null, 2)}
                  </Box>
                </Box>
              )}
            </Stack>
          </Box>
        )}
      </Drawer>
    </StyledContainer>
  );
};

export default SAJobs;
